/**
 * Client Form Page
 *
 * Form for creating and editing clients.
 *
 * @module pages/clients/ClientForm
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from 'react-bootstrap';
import { getClient, createClient, updateClient } from '../../services/clientService';
import { getClientIndustries } from '../../services/clientIndustryService';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Optional fields whose empty input must reach the DB as null, not ''.
 *
 * An untouched input holds '', and '' is not "we don't know" -- it reads as a
 * confirmed-empty answer. Storing it just moves the invented fact instead of
 * removing it (#470, same family as #362). This mirrors the `|| null` the MCP
 * lane already applies in mcp-server/src/tools/clientTools.js createClient().
 *
 * `company_name` is required (trimmed separately) and `status` is a genuine
 * lifecycle default for a record ProMedic itself creates -- neither is a claim
 * about the client's real world, so both stay out of this list.
 */
const OPTIONAL_FIELDS = [
  'industry',
  // #(auditoría 28-jul) `client_type` es la columna que la lista de clientes
  // muestra en la columna "Tipo" y por la que filtra, y hasta hoy este
  // formulario no la escribía: todo cliente creado desde la GUI salía "Sin
  // tipo" y el filtro nunca lo encontraba. Va acá porque su CHECK
  // (`clients_client_type_check`) NO admite '' — sólo los 8 valores del
  // allowlist o NULL. Mandar '' revienta el INSERT con 23514.
  'client_type',
  'primary_contact_name',
  'primary_contact_email',
  'primary_contact_phone',
  'address_line1',
  'city',
  'country',
  'notes',
  // #462: client_code is the "cliente" segment of project_number
  // (e.g. RL). Genuinely optional at client-creation time — the DB
  // function falls back to "XX" in that segment until an admin sets it.
  'client_code',
];

const CLIENT_CODE_REGEX = /^[A-Z]{2}$/;

/**
 * Tipos de cliente ofrecidos por el formulario.
 *
 * Copia EXACTA del allowlist de `clients_client_type_check` en producción
 * (`regular, vip, hospital, clinic, laboratory, pharmacy, distributor, other`).
 * Está hardcodeado a propósito y NO se deriva de `client_industries`: esa tabla
 * es editable por el admin, así que un código nuevo ahí (p. ej. 'veterinaria')
 * no existiría en el CHECK y el guardado fallaría con un 23514 opaco. Ofrecer
 * una opción condenada a fallar es peor que no ofrecerla.
 *
 * Si se agrega un tipo, primero va la migración del CHECK y después esta lista
 * (y `formatClientType` en ClientList.js, que traduce estos mismos códigos).
 */
export const CLIENT_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'vip', label: 'VIP' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'clinic', label: 'Clínica' },
  { value: 'laboratory', label: 'Laboratorio' },
  { value: 'pharmacy', label: 'Farmacia' },
  { value: 'distributor', label: 'Distribuidor' },
  { value: 'other', label: 'Otro' },
];

/**
 * Turn form state into the row we actually want to write.
 */
export const buildClientPayload = (formData) => {
  const payload = { ...formData, company_name: formData.company_name.trim() };

  OPTIONAL_FIELDS.forEach((key) => {
    const value = typeof payload[key] === 'string' ? payload[key].trim() : payload[key];
    payload[key] = value === '' || value === undefined ? null : value;
  });

  return payload;
};

const ClientForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    client_type: '',
    status: 'active',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    address_line1: '',
    city: '',
    country: '',
    notes: '',
    client_code: '',
  });

  const [errors, setErrors] = useState({});

  // Fetch client data if editing
  const { data: clientData, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClient(id),
    enabled: isEditing,
  });

  // Industries come from the admin-editable client_industries table (#461)
  // instead of a hardcoded JSX list. Only active ones are offered for new
  // selections; a client's existing value is preserved below even if it's
  // no longer in this list (inactive or never migrated).
  const { data: industries = [] } = useQuery({
    queryKey: ['client_industries'],
    queryFn: () => getClientIndustries(false),
    staleTime: 1000 * 60 * 5,
  });

  // If the client already has an industry value that isn't among the active
  // options (e.g. it was deactivated by an admin, or it's a stray legacy
  // value that predates this table), keep it selectable and visible instead
  // of silently blanking the field out from under the user.
  const industryOptions = useMemo(() => {
    const options = [...industries];
    const currentValue = formData.industry;
    if (currentValue && !options.some((opt) => opt.code === currentValue)) {
      options.push({
        id: `legacy-${currentValue}`,
        code: currentValue,
        label: `${currentValue} (no está en la lista)`,
      });
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industries, formData.industry]);

  // Populate form when client data is loaded
  useEffect(() => {
    if (clientData?.data) {
      setFormData({
        company_name: clientData.data.company_name || '',
        industry: clientData.data.industry || '',
        client_type: clientData.data.client_type || '',
        status: clientData.data.status || 'active',
        primary_contact_name: clientData.data.primary_contact_name || '',
        primary_contact_email: clientData.data.primary_contact_email || '',
        primary_contact_phone: clientData.data.primary_contact_phone || '',
        address_line1: clientData.data.address_line1 || '',
        city: clientData.data.city || '',
        country: clientData.data.country || '',
        notes: clientData.data.notes || '',
        client_code: clientData.data.client_code || '',
      });
    }
  }, [clientData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createClient,
    // #642 — createClient() ya no siempre inserta: puede devolver un
    // duplicado detectado en vez de crear una fila nueva. `variables` es el
    // payload que se mandó, así que un reintento con confirm_duplicate:true
    // no depende de que formData no haya cambiado mientras tanto.
    onSuccess: (result, variables) => {
      if (result.duplicate) {
        toast.info(
          `Ya existe un cliente con ese mismo tax_id/legal_id: ${result.data.company_name}. No se creó uno nuevo — te llevamos a su ficha.`
        );
        queryClient.invalidateQueries(['clients']);
        navigate(`/clientes/${result.data.id}`);
        return;
      }
      if (result.duplicateCandidates?.length > 0) {
        const lista = result.duplicateCandidates.map((c) => c.company_name).join(', ');
        const esOtroCliente = window.confirm(
          `Ya existe${result.duplicateCandidates.length > 1 ? 'n' : ''} un cliente con ese mismo nombre: ${lista}.\n\n`
          + '¿Confirmás que es un cliente DISTINTO y querés crearlo de todas formas?'
        );
        if (esOtroCliente) {
          createMutation.mutate({ ...variables, confirm_duplicate: true });
        }
        return;
      }
      toast.success('Cliente creado exitosamente');
      queryClient.invalidateQueries(['clients']);
      navigate(`/clientes/${result.data.id}`);
    },
    onError: (error) => {
      toast.error('Error al crear cliente: ' + error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateClient(id, data),
    onSuccess: () => {
      toast.success('Cliente actualizado exitosamente');
      queryClient.invalidateQueries(['clients']);
      queryClient.invalidateQueries(['client', id]);
      navigate(`/clientes/${id}`);
    },
    onError: (error) => {
      toast.error('Error al actualizar cliente: ' + error.message);
    },
  });

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    // #462: client_code is always 2 uppercase letters — normalize as the
    // user types instead of only rejecting on submit.
    if (name === 'client_code') {
      value = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = t('validation.required', 'Este campo es requerido');
    }

    if (formData.primary_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primary_contact_email)) {
      newErrors.primary_contact_email = t('validation.invalidEmail', 'Email inválido');
    }

    // #462: client_code is optional, but if present it must be exactly 2
    // uppercase letters — this is the "cliente" segment of project_number.
    if (formData.client_code && !CLIENT_CODE_REGEX.test(formData.client_code)) {
      newErrors.client_code = 'El código debe ser exactamente 2 letras mayúsculas (ej: RL)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('validation.fixErrors', 'Por favor corrige los errores en el formulario'));
      return;
    }

    const payload = buildClientPayload(formData);

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoadingClient) {
    return (
      <DashboardLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">{t('common.loading', 'Cargando...')}</p>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center">
              <Button
                variant="link"
                className="p-0 me-3"
                onClick={() => navigate('/clientes')}
              >
                <i className="bi bi-arrow-left fs-4"></i>
              </Button>
              <div>
                <h2 className="mb-1">
                  <i className="bi bi-building me-2"></i>
                  {isEditing
                    ? t('clients.editClient', 'Editar Cliente')
                    : t('clients.newClient', 'Nuevo Cliente')}
                </h2>
                <p className="text-muted mb-0">
                  {isEditing
                    ? t('clients.editSubtitle', 'Modifica la información del cliente')
                    : t('clients.createSubtitle', 'Ingresa la información del nuevo cliente')}
                </p>
              </div>
            </div>
          </Col>
        </Row>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* Company Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="bi bi-building me-2"></i>
                    {t('clients.companyInfo', 'Información de la Empresa')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      {/* controlId asocia Label<->Control (htmlFor/id). Sin esto el
                          input no tiene nombre accesible y los tests quedan
                          obligados a buscar por atributo `name` -- ver #472/#477. */}
                      <Form.Group controlId="client-company-name">
                        <Form.Label>{t('clients.companyName', 'Nombre de la Empresa')} *</Form.Label>
                        <Form.Control
                          type="text"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          isInvalid={!!errors.company_name}
                          placeholder={t('clients.companyNamePlaceholder', 'Ej: Hospital San José')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.company_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      {/* #462: 2-letter short code used as the "cliente" segment
                          of project_number (e.g. RL). Optional — left blank
                          falls back to "XX" until an admin sets it here. */}
                      <Form.Group controlId="client-code">
                        <Form.Label>Código</Form.Label>
                        <Form.Control
                          type="text"
                          name="client_code"
                          value={formData.client_code}
                          onChange={handleChange}
                          isInvalid={!!errors.client_code}
                          placeholder="Ej: RL"
                          maxLength={2}
                          style={{ textTransform: 'uppercase' }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.client_code}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          2 letras. Va en el número de proyecto.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {/* #461: la lista de industrias viene de client_industries,
                        administrable desde Clientes > Industrias. El controlId
                        asocia label<->input (#470). */}
                    <Col md={2}>
                      <Form.Group controlId="client-industry">
                        <Form.Label>{t('clients.industry', 'Industria')}</Form.Label>
                        <Form.Select
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                        >
                          <option value="">{t('clients.selectIndustry', 'Seleccionar industria')}</option>
                          {industryOptions.map((opt) => (
                            <option key={opt.id} value={opt.code}>{opt.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {/* Tipo de cliente. Es la columna `client_type`, que es la
                        que la lista de clientes pinta en "Tipo" y por la que
                        filtra. Este formulario no la escribía: sólo escribía
                        `industry`, así que todo cliente creado desde la web
                        aparecía "Sin tipo" e invisible para el filtro, mientras
                        los creados por el agente (carril MCP) sí la traían.
                        Deriva entre carriles, misma familia que #353. */}
                    <Col md={2}>
                      <Form.Group controlId="client-type">
                        <Form.Label>{t('clients.type', 'Tipo')}</Form.Label>
                        <Form.Select
                          name="client_type"
                          value={formData.client_type}
                          onChange={handleChange}
                        >
                          <option value="">{t('clients.selectType', 'Sin tipo')}</option>
                          {CLIENT_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>{t('clients.address', 'Dirección')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_line1"
                          value={formData.address_line1}
                          onChange={handleChange}
                          placeholder={t('clients.addressPlaceholder', 'Dirección completa')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('clients.city', 'Ciudad')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder={t('clients.cityPlaceholder', 'Ej: San José')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="client-country">
                        <Form.Label>{t('clients.country', 'País')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          placeholder={t('clients.countryPlaceholder', 'Ej: Costa Rica')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Contact Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="bi bi-person me-2"></i>
                    {t('clients.contactInfo', 'Información de Contacto')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('clients.contactName', 'Nombre del Contacto')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="primary_contact_name"
                          value={formData.primary_contact_name}
                          onChange={handleChange}
                          placeholder={t('clients.contactNamePlaceholder', 'Nombre completo')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('clients.contactPhone', 'Teléfono')}</Form.Label>
                        <Form.Control
                          type="tel"
                          name="primary_contact_phone"
                          value={formData.primary_contact_phone}
                          onChange={handleChange}
                          placeholder={t('clients.phonePlaceholder', '+506 8888-8888')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>{t('clients.contactEmail', 'Email')}</Form.Label>
                        <Form.Control
                          type="email"
                          name="primary_contact_email"
                          value={formData.primary_contact_email}
                          onChange={handleChange}
                          isInvalid={!!errors.primary_contact_email}
                          placeholder={t('clients.emailPlaceholder', 'contacto@empresa.com')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.primary_contact_email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Notes */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="bi bi-journal-text me-2"></i>
                    {t('clients.notes', 'Notas')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder={t('clients.notesPlaceholder', 'Notas adicionales sobre el cliente...')}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              {/* Status */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="bi bi-toggle-on me-2"></i>
                    {t('clients.status', 'Estado')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">{t('clients.statusActive', 'Activo')}</option>
                      <option value="inactive">{t('clients.statusInactive', 'Inactivo')}</option>
                      <option value="suspended">{t('clients.statusSuspended', 'Suspendido')}</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Actions */}
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t('common.saving', 'Guardando...')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>
                          {isEditing
                            ? t('common.saveChanges', 'Guardar Cambios')
                            : t('common.create', 'Crear Cliente')}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/clientes')}
                      disabled={isSubmitting}
                    >
                      <i className="bi bi-x-lg me-2"></i>
                      {t('common.cancel', 'Cancelar')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </DashboardLayout>
  );
};

export default ClientForm;
